<template>
    <div class="c2p-container">
        <div class="card-list" tabindex="-1">
            <h4 class="card-list__title" v-text="$ml.get('cardListTitle')" tabindex="0" />
            <div class="card-list__list-container">
                <CardListC2P
                    :card-brands="availableCardBrands"
                    :cards="cards"
                    selection-type="radioButton"
                    @card-selected="checkoutWithSelectedCard"
                    @add-card="addCard"
                    @sign-out="cardSignOut"
                />
            </div>
            <div class="card-list__inputs-container">
                <div class="card-list__input-installment">
                    <span class="card-list__input-title" v-text="$ml.get('chooseTheNumberOfInstallments')" tabindex="0" />
                    <CieloSelect
                        @change="selectItem"
                        :title="$ml.get('installment')"
                        :label="$ml.get('chooseInstallmentsNumberV2')"
                        :option-items="installmentList"
                        :selected-item="numberOfInstallments"
                        :on-get-item-event="getLangOptions"
                        :disabled="!selectedCard || installmentIsFixed"
                    />
                </div>

                <div class="card-list__input-document">
                    <span class="card-list__input-title" v-text="$ml.get('identityC2P')" tabindex="0" />
                    <CieloInput
                        :autofocus="false"
                        :borderless="false"
                        name="Identity"
                        :label="$ml.get('identityC2P')"
                        v-model="nationalIdentification"
                        v-validate="isNational ? 'required|numeric|min:11|max:11|cpf-cnpj' : 'required'"
                        :data-vv-as="$ml.get('identityC2P')"
                        :type="isNational ? 'tel' : 'text'"
                        :error="errors.first('Identity')"
                        :mask="isNational ? ['###.###.###-##'] : ''"
                        class="c2p"
                    />
                </div>
            </div>

            <div class="card-list__terms-container">
                <CieloCheckBox
                    v-if="isDofGuest"
                    v-model="agreement"
                    :clickable-slot="true"
                    :text-aria="$ml.get('termsPreffix') + $ml.get('termsSuffix')"
                >
                    <span v-text="$ml.get('termsPreffix')" />&nbsp;
                    <a target="_blank" :href="showTerms" v-text="$ml.get('termsSuffix')" tabindex="0" />
                </CieloCheckBox>

                <NewCardTermsC2P :brand-def="paymentMethodImage" v-if="cards.length == 0" />               
            </div>

            <transition name="fade">
                <CVVRequired
                    v-if="showCVVModal"
                    :security-code="securityCode"
                    @close-modal="closeModal"
                    @submit-cvv="submitCVV"
                    @input-security-code="value => (securityCode = value)"
                />
            </transition>
        </div>
        <ButtonsFinishPayment
            :disable-button-next="disableButtonNext"
            :loading="loading"
            @click-next="finishPayment"
            @click-previous="goToPreviousStep"
        />
    </div>
</template>

<script>
    const CieloInput = () => import(
        /* webpackChunkName: "cielo-input" */
        '@/components/template/CieloInput'
    );

    import vars from '@/config/vars';
    import loader from '@/helpers/loader';
    import Swal from 'sweetalert2';
    import CpfCnpjValidation from '@/helpers/validation-rules/CpfCnpjValidation';
    import PaymentProcessor from '@/services/payment/PaymentProcessor';
    import { mapState, mapGetters } from 'vuex';
    import { Token, Options } from '@/config/lang/genericOptions.js';
    import { logEvent, logException } from '@/helpers/cieloLogger';
    import CieloSelect from '@/components/template/CieloSelect/CieloSelect';
    import CardListC2P from '@/components/ClickToPay/CardListC2P';
    import ButtonsFinishPayment from '@/components/template/ButtonsFinishPayment';
    import CVVRequired from '@/components/ClickToPay/CVVRequired';
    import CieloCheckBox from '@/components/template/CieloCheckBox';
    import NewCardTermsC2P from '@/components/ClickToPay/NewCardTermsC2P';


    export default {
        components: {
            CieloInput,
            CieloSelect,
            CardListC2P,
            ButtonsFinishPayment,
            CVVRequired,
            CieloCheckBox,
            NewCardTermsC2P
        },

        data() {
            return {
                isNational: true,
                selectedCard: '',
                loading: false,
                showCVVModal: false,
                // agreement: false,
                showTerms: `${vars.CIELO_CHECKOUT_URL}/TransactionalVNext/Content/documents/Termos%20e%20condicoes%20de%20Uso_Cielo.pdf`,
            };
        },

        created() {
            loader.multiLanguage = this.$ml;
            this.$validator.validate();
            this.$validator.extend('cpf-cnpj', CpfCnpjValidation);
            this.selectedCard = '';
        },

        watch: {
            selectedCard() {
                this.doBinQuery();
            }
        },

        computed: {
            ...mapState({
                cards: state => state.clickToPayPayment.cards,
            }),
            ...mapGetters({
                selectedPaymentMethod: 'payment/getSelectedPaymentMethod',
                getInstallmentListForPaymentMethodSelected: 'payment/getInstallmentListForPaymentMethodSelected',
                installmentIsFixed: 'payment/getSelectedPaymentMethodInstallmentIsAbleToBeFixed',
                isDofGuest: 'dataOnFile/isDofGuest',
            }),
            email: {
                get: self => self.$store.state.clickToPayPayment.consumer.email,
                set(value) { this.$store.state.clickToPayPayment.consumer.email = value; }
            },
            phone: {
                get: self => self.$store.state.clickToPayPayment.consumer.phone,
                set(value) { this.$store.state.clickToPayPayment.consumer.phone = value; }
            },
            nationalIdentification: {
                get: self => self.$store.state.clickToPayPayment.consumer.nationalIdentification,
                set(value) { this.$store.state.clickToPayPayment.consumer.nationalIdentification = value; }
            },
            numberOfInstallments: {
                get: self => self.$store.getters['payment/getInstallmentNumberOfPayments'],
                set(value) { this.$store.commit('payment/setNumberOfInstallments', value); }
            },
            securityCode: {
                get: self => self.$store.state.cardPayment.SecurityCode,
                set(value) { this.$store.commit('cardPayment/setSecurityCode', value); }
            },
            agreement: {
				get: (self) => self.$store.state.customer.Agreement,
				set(value) {
					this.$store.commit('customer/setAgreement', value);
				},
			},

            installmentList() {
                if (!this.selectedPaymentMethod) {
                    return [];
                }
                return this.getInstallmentListForPaymentMethodSelected;
            },
            
            disableButtonNext() {
                if (!this.selectedCard || !this.nationalIdentification || !this.numberOfInstallments || !this.agreement || this.errors.items.length > 0) {
                    return true;
                }

                return false;
            },

            availableCardBrands() {
                if (this.$store.state.clickToPayPayment.availableCardBrands) {
                    return this.$store.state.clickToPayPayment.availableCardBrands;
                }
                return [];
            },

            paymentMethodImage() {
                return this.selectedPaymentMethod ? this.selectedPaymentMethod.Image : null;
            }
        },

        methods: {
            async checkoutWithSelectedCard(srcDigitalCardId) {
                this.selectedCard = srcDigitalCardId;
            },

            async finishPayment() {
                if(!this.loading) {
                    this.loading = true;

                    const popup = window.open('', 'mc_c2p', 'width=600,height=600');
                    if(!popup) {
                        this.showErrorMessage('Habilite pop-ups para finalizar seu pagamento');
                        this.loading = false;
                        return;
                    }
                    
                    await this.$store.dispatch('clickToPayPayment/checkoutWithCard', {
                        srcDigitalCardId: this.selectedCard,
                        windowRef: popup
                    });

                    const responseCode = this.$store.state.clickToPayPayment.checkoutResponse.actionCode;

                    try {
                        popup.close();
                    } catch (error) {
                        logException(error);
                    }

                    if (responseCode !== 'COMPLETE') {
                        this.showErrorMessage();
                        this.loading = false;
                        return;
                    }

                    try {
                        await PaymentProcessor.Process();
                        this.loading = false;

                        this.finishGAEvent();
                    } catch (error) {
                        if (error.message === 'cvvRequiredError') {
                            this.showCVVModal = true;
                        }
                        logException(error);
                        this.loading = false;
                    }
                }
            },

            showErrorMessage() {
                Swal.fire({
                    type: 'error',
                    title: this.$ml.get('errorCheckoutHeading'),
                    text: this.$ml.get('errorCheckoutBody'),
                    confirmButtonText: this.$ml.get('retry'),
                    cancelButtonText: this.$ml.get('back'),
                    showCancelButton: true,
                    allowOutsideClick: false,
                    allowEscapeKey: false
                }).then(({ dismiss }) => {
                    if (dismiss === 'cancel') {
                        this.$store.dispatch('pipeline/pushTo', 'ChangePaymentType');
                    }
                });
            },

            addCard() {
                this.$store.dispatch('pipeline/pushTo', 'UnifiedCardPaymentC2P');
            },

            goToPreviousStep() {
                this.$store.dispatch('pipeline/pushTo', 'UserValidationC2P');

                this.$store.state.clickToPayPayment.consumerPresent = false;
                this.$store.state.clickToPayPayment.otpValidation.error = '';
                this.$store.state.clickToPayPayment.consumer.email = '';
            },

            async cardSignOut() {
                await window.mcCheckoutService.signOut();
                this.$store.state.clickToPayPayment.consumerPresent = false;
                this.$store.state.clickToPayPayment.cards = [];
                this.resetConsumersContactData();
                this.$store.dispatch('pipeline/pushTo', 'ChangePaymentType');
            },

            async doBinQuery() {
                try {
                    let card = this.cards.find(crd => crd.srcDigitalCardId === this.selectedCard) || {};

                    if (!card.panBin) {
                        const defaultMasterBin = 518600;
                        const defaultVisaBin = 462294;
                        const defaultBin = card.paymentCardDescriptor === 'visa' ? defaultVisaBin : defaultMasterBin;
                        await this.$store.dispatch('clickToPayPayment/fetchCardBrand', defaultBin);
                        return true;
                    }

                    await this.$store.dispatch('clickToPayPayment/fetchCardBrand', card.panBin);
                    return true;
                }
                catch (error) {
                    logEvent({
                        category: 'BinQuery',
                        action: 'Do Bin Query',
                        label: `[BIN QUERY ERROR]: ${error}`
                    });

                    const cardBrandError = this.$store.state.clickToPayPayment.cardBrandError;

                    await Swal.fire({
                        type: 'error',
                        title: 'Oppsss!',
                        text: cardBrandError ?
                            this.$ml.get(cardBrandError) :
                            this.$ml.get('processingRequestError'),
                        confirmButtonText: this.$ml.get('retry'),
                        cancelButtonText: this.$ml.get('cancel'),
                        showCancelButton: cardBrandError ? false : true,
                        showLoaderOnConfirm: cardBrandError ? false : true,
                        preConfirm: cardBrandError ? null : this.doBinQuery,
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    });
                    return false;
                }
            },

            selectItem(item) {
                this.numberOfInstallments = item;
            },

            getLangOptions(item) {
                let tokens = [];

                tokens.push(new Token('quantity', item.Value));
                tokens.push(new Token('installmentPrice', item.installmentPrice));

                return new Options('installmentsItem', tokens);
            },

            resetConsumersContactData() {
                this.email = '';
                this.phone = '';
            },

            closeModal() {
                this.showCVVModal = false;
            },

            async submitCVV() {
                if(!this.loading) {
                      this.showCVVModal = false;
                      this.loading = true;
                      await PaymentProcessor.Process();
                      this.loading = false;

                      this.finishGAEvent();
                }
            },

            finishGAEvent() {
                logEvent({
                    category: 'click_to_pay',
                    action: 'finalizacao_cardlist_c2p',
                    label: 'Usuário escolhe cartão já salvo na lista de cartões e clica em CONCLUIR COMPRA.'
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .card-list {
        margin-bottom: 24px;
        
        &__title {
            display: block;
            font-size: 16px;
            font-weight: 700;
            margin-bottom: 8px;
            color: #2F363E;
        }

        &__list-container {
            margin-bottom: 8px;
        }

        &__input-document {
            margin-bottom: 24px;
        }

        &__input-installment {
            margin-bottom: 32px;
        }

        &__input-title {
            display: block;
            font-size: 16px;
            font-weight: 700;
            color: #2F363E;
            margin-bottom: 16px;

            @media screen and (min-width: 768px) {}
        }
    }

    .nct {
        margin-top: 20px;
    }
</style>
